import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If on the Google OAuth callback page, skip /me check — AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.pathname === "/auth/google/callback") {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, recaptchaToken) => {
    const { data } = await api.post("/auth/login", { email, password }, {
      headers: recaptchaToken ? { "X-Recaptcha-Token": recaptchaToken } : {},
    });
    setUser(data);
    return data;
  };

  const register = async (name, email, password, recaptchaToken) => {
    const { data } = await api.post("/auth/register", { name, email, password }, {
      headers: recaptchaToken ? { "X-Recaptcha-Token": recaptchaToken } : {},
    });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setUser(null);
  };

  const value = { user, loading, login, register, logout, refresh: checkAuth, setUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
